import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useLoaderData } from 'react-router';
import { EcommerceEventTypeV4 } from '../../common/analytics';
import { customerMsg, productsMsg, t } from '../../common/i18n';
import { getCompanyName } from '../../common/utils/accountUtils';
import { scrollTo } from '../../common/utils/browserUtils';
import { getSubscriptionDetailForDeviceChangeFromSessionStorage } from '../../common/utils/checkoutUtils';
import { getContactInfo } from '../../common/utils/stateUtils';
import { Order } from '../../generated/api/order';
import { getActiveAccountMasterId } from '../../selfservice/common/localStorageUtils';
import { Loading } from '../Loading';
import { NotificationContainer } from '../NotificationContainer/NotificationContainer.js';
import {
  getCommercialProductForGuid,
  getOfferForBasketItem,
  getOnlineModelForBasketItem,
  isItemDiscontinued,
} from '../ShoppingBasket/shoppingBasketUtils';
import { usePlaceOrder } from './hooks/usePlaceOrder';
import { useUnavailableNumbersHandler } from './hooks/useUnavailableNumbersHandler';
import { PlaceOrderModals } from './modals/PlaceOrderModals';
import {
  type CampaignData,
  type PlaceOrderFormData,
  PlaceOrderStep,
  type PlaceOrderStepState,
  STEP_ORDER,
  type ShoppingBasketUpdateMethods,
} from './placeOrderTypes';
import { CustomerStep } from './steps/CustomerStep';
import { ProductsStep } from './steps/ProductsStep';
import { PlaceOrderSummary } from './steps/common/PlaceOrderSummary';
import { DeliveryStep } from './steps/delivery/DeliveryStep';
import { ConfirmModal } from './steps/payment/ConfirmModal';
import { PaymentStep } from './steps/payment/PaymentStep';
import { getApplicableEnrollmentProgram } from './steps/products/DeviceAddonsView';
import { buildValidatedAddOnsForBasketItem } from './utils/addOnValidationUtils';
import { isCardPaymentPossible } from './utils/cardPaymentUtils.js';
import { updateContacts } from './utils/contactOrPurposeUtils';
import { getAddons, handleBasketCheckoutAnalytics } from './utils/placeOrderUtils';
import type { MergedDeliveryMethod } from './utils/placeOrderUtils';
import type { PlaceOrderLoaderData } from '../../common/loaders';
import type { ShoppingBasketType } from '../../common/types/shoppingBasket';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { Contact } from '../../generated/api/contact';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { Subscription } from '../../generated/api/subscription';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { AuthenticatedUser } from '../../public/site/authState';

import './PlaceOrder.scss';

const EMPTY_ASSOCIATIONS: AssociationRecord[] = [];
const EMPTY_DEPENDENCIES: DependencyRecord[] = [];

export interface PlaceOrderProps {
  shoppingBasket: ShoppingBasketType;
  onlineModels: OnlineModel[];
  shoppingBasketUpdateMethods: ShoppingBasketUpdateMethods;
  user: AuthenticatedUser;
  deliveryMethods?: MergedDeliveryMethod[];
  companyInfo: CompanyInfoResponse;
  mdmId: string;
  campaignData?: CampaignData;
  expiringEppSubscriptions: Subscription[];
}

/**
 * Build default form values from shopping basket items.
 * Creates one orderItem per basket item quantity for assignment to users.
 * Validates add-ons using CloudSense rules for SELF_SERVICE subscriptions.
 */
const buildDefaultFormValues = (
  onlineModels: OnlineModel[],
  shoppingBasket: ShoppingBasketType,
  companyInfo: CompanyInfoResponse,
  associations: AssociationRecord[] = [],
  dependencies: DependencyRecord[] = [],
  deviceChangeContactId?: string
): PlaceOrderFormData => {
  let isFirstUnit = true;
  return {
    products: {
      placeOrderItemUnits:
        shoppingBasket.items
          ?.filter(basketItem => !isItemDiscontinued(basketItem, onlineModels))
          .flatMap(basketItem => {
            const onlineModelForItem = getOnlineModelForBasketItem(onlineModels, basketItem)!;
            const commercialProduct = getCommercialProductForGuid(
              basketItem.offer.commercialProductGuid,
              onlineModelForItem
            )!;
            const offer = getOfferForBasketItem(onlineModels, basketItem);
            const enrollmentProgram = offer ? getApplicableEnrollmentProgram(offer, companyInfo) : undefined;
            return (
              Array.from({ length: basketItem.quantity || 1 }, (_, index) => {
                // Use CloudSense validation for SELF_SERVICE subscriptions when rules are available
                const childOrderItems =
                  associations.length > 0
                    ? buildValidatedAddOnsForBasketItem(
                        basketItem,
                        index,
                        commercialProduct,
                        onlineModelForItem,
                        associations,
                        dependencies
                      )
                    : (basketItem.offer.addOns && getAddons(commercialProduct, basketItem.offer.addOns[index])) || [];
                // Pre-populate userContact for the first unit if entering via device change flow
                const userContact = isFirstUnit && deviceChangeContactId ? deviceChangeContactId : undefined;
                isFirstUnit = false;
                return {
                  basketItemId: basketItem.id,
                  basketItemIndex: index,
                  addons: childOrderItems,
                  contactId: undefined,
                  purposeOfUse: undefined,
                  enrollmentProgram,
                  userContact,
                };
              }) ?? []
            );
          }) ?? [],
    },
    delivery: {},
    payment: {
      paymentType: Order.PaymentTypeEnum.BILLING_ACCOUNT,
    },
  };
};

export const PlaceOrder = (props: PlaceOrderProps) => {
  const { mdmId, shoppingBasket, onlineModels, user, deliveryMethods, companyInfo, campaignData } = props;

  // Get add-on rules from loader data for SELF_SERVICE subscription validation
  // Loader runs once before component mount, so addOnRules is stable and won't change
  const { contacts: initialContacts, addOnRules } = useLoaderData() as PlaceOrderLoaderData;
  // Derived arrays used in useEffect - use stable empty constants to prevent unnecessary re-runs
  const associations = (addOnRules?.associations?.[0] as AssociationRecord[]) ?? EMPTY_ASSOCIATIONS;
  const dependencies = (addOnRules?.dependencies?.[0] as DependencyRecord[]) ?? EMPTY_DEPENDENCIES;

  // Read device change context from sessionStorage (set by EppDeviceChangeDialog)
  const [subscriptionDetailForDeviceChange] = useState(() => getSubscriptionDetailForDeviceChangeFromSessionStorage());

  const [activeStep, setActiveStep] = useState<PlaceOrderStep>(PlaceOrderStep.CUSTOMER);
  const [stepState, setStepState] = useState<PlaceOrderStepState>({
    completed: new Set(),
  });
  const [isCreatingNewBillingAccount, setIsCreatingNewBillingAccount] = useState(false);
  const [pendingStep, setPendingStep] = useState<PlaceOrderStep | undefined>(undefined);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showOrderErrorModal, setShowOrderErrorModal] = useState(false);
  const [showCreditCheckModal, setShowCreditCheckModal] = useState(false);

  const [contacts, setContacts] = useState<Contact[]>(initialContacts.contacts || []);

  const refreshContacts = async () => {
    try {
      const response = await updateContacts();
      setContacts(response.contacts ?? []);
    } catch {
      // Silently continue - form still has the correct contactId even if refresh fails
    }
  };

  const contactInfo = getContactInfo(user);
  const companyName = getCompanyName(user, getActiveAccountMasterId());
  const businessId = companyInfo?.businessId ?? '';

  const {
    unavailableNumbers,
    showUnavailableNumbersModal,
    isReplacingNumbers,
    handleNumbersUnavailable,
    handleReplaceAndSubmit,
    handleCancelNumberReplacement,
    setSubmitOrder,
  } = useUnavailableNumbersHandler();

  const { isSubmittingOrder, submitOrder } = usePlaceOrder({
    shoppingBasket,
    authenticatedUser: user,
    onlineModels,
    businessId,
    campaignData,
    associations,
    dependencies,
    onNumbersUnavailable: handleNumbersUnavailable,
    onError: () => setShowOrderErrorModal(true),
    onCreditCheckFailure: () => setShowCreditCheckModal(true),
  });

  // Connect submitOrder to the unavailable numbers handler
  setSubmitOrder(submitOrder);

  // Build initial form values (without validation - rules may not be loaded yet)
  // Pre-populate first item's contact if entering via device change flow
  const initialFormValues = buildDefaultFormValues(
    onlineModels,
    shoppingBasket,
    companyInfo,
    undefined,
    undefined,
    subscriptionDetailForDeviceChange?.contactId
  );

  // Initialize form with default values from shopping basket
  const methods = useForm<PlaceOrderFormData>({
    defaultValues: initialFormValues,
    mode: 'onChange',
  });
  const { control, watch } = methods;
  const deliveryCharge = watch('delivery.deliveryPriceInCents') ?? 0;
  useFieldArray({ control, name: 'products.placeOrderItemUnits' });

  // Derive device change billing account ID from form state
  // When an EPP device change is selected, the billing account must stay locked to the replaced subscription's BA
  const placeOrderItemUnits = watch('products.placeOrderItemUnits');
  const deviceChangeBillingAccountId = placeOrderItemUnits?.find(
    unit => unit.deviceChange?.replacedSubscriptionBillingAccountId
  )?.deviceChange?.replacedSubscriptionBillingAccountId;

  // Sync form state when shopping basket changes or add-on rules finish loading
  // Merge existing user-edited values with new structure by matching on basketItemId + basketItemIndex
  // Applies CloudSense add-on validation for SELF_SERVICE subscriptions
  useEffect(() => {
    const currentValues = methods.getValues();
    const newDefaultValues = buildDefaultFormValues(
      onlineModels,
      shoppingBasket,
      companyInfo,
      associations,
      dependencies
    );

    // Preserve existing orderItem values by matching on basketItemId and basketItemIndex
    // but use validated add-ons from the new default values
    const mergedOrderItemUnits = newDefaultValues.products.placeOrderItemUnits.map(newItem => {
      const existingItem = currentValues.products?.placeOrderItemUnits?.find(
        existing =>
          existing.basketItemId === newItem.basketItemId && existing.basketItemIndex === newItem.basketItemIndex
      );

      if (existingItem) {
        // Preserve user-edited addon CODES (don't let CloudSense re-add removed addons)
        // but use basket-validated ATTRIBUTES (e.g. voicemail language) from newItem.
        const existingAddonCodes = new Set(existingItem.addons.map(a => a.addOnCode));
        return {
          ...existingItem,
          addons: newItem.addons.filter(addon => existingAddonCodes.has(addon.addOnCode)),
        };
      }
      return newItem;
    });

    methods.reset(
      {
        ...currentValues,
        products: {
          placeOrderItemUnits: mergedOrderItemUnits,
        },
      },
      { keepDefaultValues: true }
    );
  }, [shoppingBasket, onlineModels, methods, associations, dependencies, companyInfo]);

  // Track BEGIN_CHECKOUT analytics event once on mount
  useEffect(() => {
    handleBasketCheckoutAnalytics(shoppingBasket, EcommerceEventTypeV4.BEGIN_CHECKOUT, onlineModels);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const moveToStep = (targetStep: PlaceOrderStep) => {
    if (targetStep === activeStep) {
      return;
    }

    // If user is creating a new billing account on payment step, show confirmation modal
    if (isCreatingNewBillingAccount && activeStep === PlaceOrderStep.PAYMENT) {
      setPendingStep(targetStep);
      setShowConfirmModal(true);
      return;
    }

    const currentIndex = STEP_ORDER.indexOf(activeStep);
    const targetIndex = STEP_ORDER.indexOf(targetStep);

    // Allow backward navigation
    if (targetIndex < currentIndex) {
      setActiveStep(targetStep);
      scrollTo(0);
    }
  };

  const handleNextStep = async () => {
    // Mark current step as completed when moving to next step
    setStepState(prev => ({
      ...prev,
      completed: new Set([...prev.completed, activeStep]),
    }));

    const currentIndex = STEP_ORDER.indexOf(activeStep);
    const nextStep = STEP_ORDER[currentIndex + 1];

    // Track analytics for step transitions
    if (nextStep) {
      if (nextStep === PlaceOrderStep.DELIVERY) {
        handleBasketCheckoutAnalytics(shoppingBasket, EcommerceEventTypeV4.ADD_SHIPPING_INFO, onlineModels);
      } else if (nextStep === PlaceOrderStep.PAYMENT) {
        handleBasketCheckoutAnalytics(shoppingBasket, EcommerceEventTypeV4.ADD_PAYMENT_INFO, onlineModels);
      }

      setActiveStep(nextStep);
      scrollTo(0);
    }
  };

  const handlePreviousStep = () => {
    const currentIndex = STEP_ORDER.indexOf(activeStep);
    const previousStep = STEP_ORDER[currentIndex - 1];

    if (previousStep) {
      setActiveStep(previousStep);
      scrollTo(0);
    }
  };

  const handleConfirmNavigation = () => {
    setShowConfirmModal(false);
    setIsCreatingNewBillingAccount(false);
    if (pendingStep) {
      setActiveStep(pendingStep);
      setPendingStep(undefined);
      scrollTo(0);
    }
  };

  const handleCancelNavigation = () => {
    setShowConfirmModal(false);
    setPendingStep(undefined);
  };

  return (
    <FormProvider {...methods}>
      <NotificationContainer />
      <form onSubmit={methods.handleSubmit(submitOrder)} className="of-place-order">
        <CL.Stepper
          orientation="horizontal"
          sticky={true}
          steps={[
            {
              id: PlaceOrderStep.CUSTOMER,
              name: t.IY5V(customerMsg),
              content: <CustomerStep companyName={companyName} contactInfo={contactInfo} onNext={handleNextStep} />,
              icon: <CL.Icon icon="office" />,
              onClick: () => moveToStep(PlaceOrderStep.CUSTOMER),
              completed: stepState.completed.has(PlaceOrderStep.CUSTOMER),
              disabled: false, // First step is always accessible
            },
            {
              id: PlaceOrderStep.PRODUCTS,
              name: t.IY4Z(productsMsg),
              content: (
                <ProductsStep
                  {...props}
                  contactsUpdateMethods={{
                    contacts,
                    onContactCreated: refreshContacts,
                  }}
                  onNext={handleNextStep}
                  onPrevious={handlePreviousStep}
                  orderSummary={<PlaceOrderSummary {...props} defaultOpen={false} deliveryCharge={deliveryCharge} />}
                />
              ),
              icon: <CL.Icon icon="group" />,
              onClick: () => moveToStep(PlaceOrderStep.PRODUCTS),
              completed: stepState.completed.has(PlaceOrderStep.PRODUCTS),
              disabled: STEP_ORDER.indexOf(PlaceOrderStep.PRODUCTS) > STEP_ORDER.indexOf(activeStep),
            },
            {
              id: PlaceOrderStep.DELIVERY,
              name: t.BSE3('Delivery'),
              content:
                deliveryMethods && companyInfo ? (
                  <DeliveryStep
                    {...props}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                    company={companyInfo}
                    deliveryMethods={deliveryMethods}
                    user={user}
                    orderSummary={<PlaceOrderSummary {...props} defaultOpen={false} deliveryCharge={deliveryCharge} />}
                  />
                ) : (
                  <Loading />
                ),
              icon: <CL.Icon icon="delivery" />,
              onClick: () => moveToStep(PlaceOrderStep.DELIVERY),
              completed: stepState.completed.has(PlaceOrderStep.DELIVERY),
              disabled: STEP_ORDER.indexOf(PlaceOrderStep.DELIVERY) > STEP_ORDER.indexOf(activeStep),
            },
            {
              id: PlaceOrderStep.PAYMENT,
              name: t.D9Q4('Payment'),
              content: (
                <PaymentStep
                  contactInfo={contactInfo}
                  isCardPaymentPossible={isCardPaymentPossible(shoppingBasket, onlineModels, deliveryCharge)}
                  onCreatingNewBillingAccountChange={setIsCreatingNewBillingAccount}
                  orderSummary={<PlaceOrderSummary {...props} defaultOpen={true} deliveryCharge={deliveryCharge} />}
                  isSubmittingOrder={isSubmittingOrder}
                  onSubmit={methods.handleSubmit(submitOrder)}
                  onPrevious={handlePreviousStep}
                  mdmId={mdmId}
                  deviceChangeBillingAccountId={deviceChangeBillingAccountId}
                />
              ),
              icon: <CL.Icon icon="money" />,
              onClick: () => moveToStep(PlaceOrderStep.PAYMENT),
              completed: stepState.completed.has(PlaceOrderStep.PAYMENT),
              disabled: STEP_ORDER.indexOf(PlaceOrderStep.PAYMENT) > STEP_ORDER.indexOf(activeStep),
            },
          ]}
          activeStep={activeStep}
        />
        {showConfirmModal && <ConfirmModal onStay={handleCancelNavigation} onContinue={handleConfirmNavigation} />}
        <PlaceOrderModals
          isSubmittingOrder={isSubmittingOrder}
          showOrderErrorModal={showOrderErrorModal}
          showCreditCheckModal={showCreditCheckModal}
          showUnavailableNumbersModal={showUnavailableNumbersModal}
          unavailableNumbers={unavailableNumbers}
          isReplacingNumbers={isReplacingNumbers}
          onCloseOrderErrorModal={() => setShowOrderErrorModal(false)}
          onCloseCreditCheckModal={() => setShowCreditCheckModal(false)}
          onConfirmNumberReplacement={handleReplaceAndSubmit}
          onCancelNumberReplacement={handleCancelNumberReplacement}
        />
      </form>
    </FormProvider>
  );
};
